import { Link } from "react-router-dom";
import { Activity, Beaker, Building2, Check, ChevronDown, Globe, Keyboard, Moon, Search, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { TOPOLOGIES } from "@/data/topologies";
import {
  useAdapter,
  useAvailableSites,
  useConnection,
  useSiteStore,
  useSnapshot,
  useTopology,
} from "@/store/site";
import { useNow } from "@/hooks/useNow";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { freshnessBucket } from "@/lib/selectors/freshnessBucket";
import { formatTimeOfDay } from "@/lib/format";
import { SUPPORTED_LOCALES, setLanguage, useLanguage, useT, type Lang } from "@/lib/i18n";
import { SmartEMark } from "@/components/ui/SmartEMark";
import { PendingWritesPip } from "@/components/PendingWritesPip";
import { retryLive } from "@/lib/connectionRecovery";

/**
 * Slim single-row topbar. Brand mark + site picker on the left; command
 * palette + data-source pill + theme toggle on the right. Section
 * navigation lives in the two-tier ViewTabs (rendered by PageShell), not
 * here — and no left sidebar, since TB provides one at the host level.
 */
export function Topbar({
  onPaletteOpen,
  onShortcutsOpen,
}: {
  onPaletteOpen: () => void;
  onShortcutsOpen: () => void;
}) {
  // Three themes exist: "light", "dark", and "black" (a neutral true-black
  // variant set by the host widget to match TB's chrome). The in-app toggle is
  // only a light/dark switch — it never SETS black — but it must PRESERVE a
  // widget-set black on mount (initialise from data-theme, don't clobber it).
  const [theme, setTheme] = useState<"dark" | "light" | "black">(() => {
    if (typeof document === "undefined") return "light";
    const t = document.documentElement.dataset.theme;
    return t === "dark" ? "dark" : t === "black" ? "black" : "light";
  });
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    // Keep UA-painted chrome (native select/date/time popups) in sync with the
    // toggle — mirrors the pre-paint pin in index.html. "black" is a dark variant.
    document.documentElement.style.colorScheme = theme === "light" ? "light" : "dark";
  }, [theme]);

  const macish = typeof navigator !== "undefined" && /(Mac|iPhone|iPad|iPod)/i.test(navigator.platform);
  const cmdKey = macish ? "⌘" : "Ctrl";
  const t = useT();

  return (
    <header className="brand-banner sticky top-0 z-30 border-b border-edge">
      {/* Brand + site picker (left) · command palette + status + theme (right). */}
      <div className="flex h-12 items-center justify-between px-3 sm:px-4">
        <div className="flex min-w-0 items-center gap-3">
          <Link to={carryTo("/overview")} className="flex shrink-0 items-center gap-2" aria-label={t("common.operatorConsoleHome")}>
            <SmartEMark size={22} />
            <span className="hidden text-[11px] font-semibold uppercase tracking-[0.18em] text-text-muted sm:inline">
              {t("common.operatorConsole")}
            </span>
          </Link>
          <span className="hidden h-5 w-px bg-edge sm:inline-block" />
          <SiteSwitcher />
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={onPaletteOpen}
            className="hidden h-8 items-center gap-2 rounded-md border border-edge bg-surface px-2.5 text-[11px] text-text-muted hover:border-accent hover:text-text md:flex"
            title={t("common.openCommandPalette")}
          >
            <Search size={12} />
            <span>{t("common.command")}</span>
            <span className="ml-2 rounded border border-edge bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-text-faint">{cmdKey} K</span>
          </button>
          <button
            onClick={onPaletteOpen}
            className="grid h-8 w-8 place-items-center rounded-md border border-edge bg-surface text-text-muted hover:text-text md:hidden"
            aria-label={t("common.openCommandPalette")}
          >
            <Search size={14} />
          </button>
          {/* F21 — pending-writes pip. Idle-invisible (renders null when the
              shared store is empty), a NEW region distinct from the data-source
              pill below and the G33 retry inside it. */}
          <PendingWritesPip />
          <DataSourcePill />
          <button
            onClick={onShortcutsOpen}
            className="hidden h-8 w-8 place-items-center rounded-md border border-edge bg-surface text-text-muted hover:text-text md:grid"
            aria-label={t("common.keyboardShortcuts")}
            title={`${t("common.keyboardShortcuts")} (?)`}
          >
            <Keyboard size={14} />
          </button>
          <LanguageMenu />
          <button
            // Light/dark switch only. From "black" (a dark variant, widget-set)
            // this drops to "light"; black is never reachable from the toggle.
            // The explicit choice is persisted to localStorage "ems-theme"
            // (DATA-11/PERS-08) and re-applied by the index.html pre-paint
            // script — only values this toggle itself sets are ever stored.
            onClick={() => {
              const next = theme === "light" ? "dark" : "light";
              try { localStorage.setItem("ems-theme", next); } catch { /* sandboxed storage */ }
              setTheme(next);
            }}
            className="grid h-8 w-8 place-items-center rounded-md border border-edge bg-surface text-text-muted hover:text-text"
            aria-label={t("common.toggleTheme")}
            title={theme === "light" ? t("common.switchToDark") : t("common.switchToLight")}
          >
            {theme === "light" ? <Moon size={14} /> : <Sun size={14} />}
          </button>
        </div>
      </div>
    </header>
  );
}

/**
 * Native names, never translated — each language must be recognisable to
 * a speaker who is currently stuck in the WRONG language.
 */
const LANGUAGE_NAMES: Record<Lang, string> = {
  en: "English",
  it: "Italiano",
  de: "Deutsch",
  es: "Español",
};

/**
 * Maps each adapter capability flag to the i18n key for its plain-language
 * "not available on this connection" row (G32). Module-level so it isn't
 * re-allocated per render and so adding a future flag is a dict-key change,
 * not new code. Only flags that are currently FALSE render a row.
 */
const CAP_LABEL_KEYS: Record<string, string> = {
  snoozeAlarms: "topbar.cap.snoozeAlarms",
  alarmComments: "topbar.cap.alarmComments",
};

/**
 * Globe language menu — small dropdown listing the supported languages,
 * the active one checked. Same backdrop+panel pattern as SiteSwitcher.
 * setLanguage persists to localStorage "ems-lang" and every useT()
 * consumer re-renders immediately.
 */
function LanguageMenu() {
  const t = useT();
  const lang = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        className="grid h-8 w-8 place-items-center rounded-md border border-edge bg-surface text-text-muted hover:text-text"
        aria-label={t("common.language")}
        title={t("common.language")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Globe size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-50 min-w-[170px] overflow-hidden rounded-md border border-edge bg-surface shadow-xl">
            <div className="border-b border-edge px-3 py-2 text-[10px] uppercase tracking-wider text-text-faint">{t("common.language")}</div>
            {SUPPORTED_LOCALES.map((l) => (
              <button
                key={l}
                onClick={() => { setLanguage(l); setOpen(false); }}
                className={cn(
                  "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-surface-2",
                  l === lang && "bg-accent/10 text-accent",
                )}
              >
                <span className="font-semibold">{LANGUAGE_NAMES[l]}</span>
                {l === lang && <Check size={12} />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Data-source pill + popover (DATA-12).
 *
 * - Operator language ("Simulated", "Connection error", "Reconnecting…")
 *   instead of the developer jargon "Mock" / "Live·err".
 * - Below `sm` the full pill used to disappear entirely; a dot-only
 *   variant (colored pip + sr-only label) is now always visible, so
 *   simulated data can never be indistinguishable from live on a narrow
 *   TB dashboard column or tablet.
 * - Clicking either variant opens a popover (same backdrop+panel pattern
 *   as SiteSwitcher) with the source explanation, last-data timestamp,
 *   the connectionError text, and — only when the live connection failed —
 *   a Retry button reusing ConnectingSplash's recovery dance (drop ?mock,
 *   assert ?live=1, full reload so boot-time discovery re-runs).
 */
function DataSourcePill() {
  const now = useNow(1000);
  const { connection, error } = useConnection();
  const snapTs = useSiteStore((s) => s.snapshot.ts);
  const snapConnection = useSnapshot().connection;
  const [open, setOpen] = useState(false);
  const t = useT();

  // G32 — surface the capabilities this connection LACKS in the popover.
  // adapter may be null before the first connect / during connecting, so
  // guard it; data-driven from adapter.capabilities (same shape in mock
  // and TB), FALSE-only (a true cap renders nothing).
  const adapter = useAdapter();
  const caps = adapter?.capabilities;
  const disabledCaps = caps
    ? (Object.keys(CAP_LABEL_KEYS) as (keyof typeof caps)[]).filter((k) => caps[k] === false)
    : [];

  // Pill colour reflects the actual live-data state, independent of the
  // user-requested mode. "reconnecting" is amber: data WAS live and the
  // socket is down — the values on screen are aging (cluster B widens the
  // store union; the case is rendered here).
  //
  // G29: the live/connecting/reconnecting/error/mock tones flow from the
  // shared freshnessBucket() projection so the pill can never drift from
  // the TelemetryOutageStrip's tone vocabulary. Demo stays G30's warn tone
  // (freshnessBucket returns no tone for demo, by design), so the ternary
  // keeps its own demo branch and falls back to the bucket tone otherwise.
  const view = freshnessBucket({ connection, snapshot: snapConnection, hasError: !!error });
  const tone =
    connection === "demo"
      ? "text-warn border-warn/30 bg-warn/10"
      : view.tone ?? "text-text-muted border-edge bg-surface-2";
  const label =
    connection === "live"       ? t("status.live")
    : connection === "connecting"   ? t("status.connecting")
    : connection === "reconnecting" ? t("status.reconnecting")
    : connection === "error"        ? t("status.connectionError")
    : connection === "demo"         ? t("status.demo")
    : t("status.simulatedShort");
  const explanation =
    connection === "live"       ? t("topbar.explain.live")
    : connection === "connecting"   ? t("topbar.explain.connecting")
    : connection === "reconnecting" ? t("topbar.explain.reconnecting")
    : connection === "error"        ? t("topbar.explain.error")
    : connection === "demo"         ? t("topbar.explain.demo")
    : t("topbar.explain.mock");

  // Recovery is the one shared seam now (G33): retryLive drops ?mock,
  // re-asserts ?live=1, and full-reloads to re-run TB discovery. See
  // lib/connectionRecovery (same dance ConnectingSplash + ErrorBoundary use).

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        className={cn(
          "hidden h-8 items-center gap-2 rounded-md border px-2.5 text-[11px] font-medium uppercase tracking-wider sm:flex",
          tone,
        )}
        title={error ?? `connection: ${connection}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="pip live bg-current" />
        {label} · {formatTimeOfDay(now)}
      </button>
      {/* Dot-only compact variant — the data source indicator must never
          be invisible, even below sm (DATA-12). */}
      <button
        onClick={() => setOpen((x) => !x)}
        className={cn("grid h-8 w-8 place-items-center rounded-md border sm:hidden", tone)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="pip live bg-current" />
        <span className="sr-only">{t("topbar.dataSource")}: {label}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-50 w-[300px] overflow-hidden rounded-md border border-edge bg-surface shadow-xl">
            <div className="border-b border-edge px-3 py-2 text-[10px] uppercase tracking-wider text-text-faint">{t("topbar.dataSource")}</div>
            <div className="px-3 py-3">
              <div className="text-xs font-semibold">{label}</div>
              <p className="mt-1 text-[11px] leading-relaxed text-text-muted">{explanation}</p>
              <div className="mt-2 text-[11px] text-text-muted">
                {t("topbar.lastData")} <span className="num text-text">{formatTimeOfDay(snapTs)}</span>
              </div>
              {connection !== "error" && disabledCaps.length > 0 && (
                <div className="mt-3 border-t border-edge pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-text-faint">{t("topbar.cap.heading")}</div>
                  <ul className="mt-1 space-y-1">
                    {disabledCaps.map((k) => (
                      <li key={k} className="flex items-start gap-1.5 text-[11px] text-text-muted">
                        <span aria-hidden className="mt-[3px] h-px w-2 shrink-0 bg-text-faint" />
                        <span>{t(CAP_LABEL_KEYS[k])}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {error && (
                <div
                  className={cn(
                    "mt-2 rounded-md border px-2 py-1.5 text-[10px]",
                    connection === "error"
                      ? "border-critical/30 bg-critical/10 text-critical"
                      : "border-edge bg-surface-2 text-text-muted",
                  )}
                >
                  {error}
                </div>
              )}
              {connection === "error" && (
                <button
                  onClick={retryLive}
                  className="mt-3 flex h-8 w-full items-center justify-center rounded-md border border-accent/40 bg-accent/15 text-xs font-semibold text-accent hover:bg-accent/25"
                >
                  {t("topbar.retryLive")}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SiteSwitcher() {
  // Select primitives separately so Zustand v5 doesn't infinite-loop on
  // new-object returns (#185).
  const topologyId = useSiteStore((s) => s.topologyId);
  const setSite = useSiteStore((s) => s.setSite);
  const overall = useSiteStore((s) => s.snapshot.health.overall);
  const mode = useSiteStore((s) => s.mode);
  const topology = useTopology();
  const availableSites = useAvailableSites();
  const [open, setOpen] = useState(false);
  const t = useT();

  const isLive = mode !== "mock";
  const currentName = isLive
    ? topology?.name ?? "—"
    : (TOPOLOGIES.find((topo) => topo.id === topologyId)?.name ?? topology?.name ?? "—");
  const liveCanSwap = isLive && availableSites.length > 1;

  return (
    <div className="relative min-w-0">
      <button
        onClick={() => setOpen((x) => !x)}
        className="flex h-8 min-w-0 items-center gap-2 rounded-md border border-edge bg-surface px-2.5 text-xs hover:border-accent"
      >
        <Building2 size={13} className="shrink-0 text-accent" />
        <span className="truncate font-semibold">{currentName}</span>
        <span className="hidden text-text-faint sm:inline">·</span>
        <span className={cn(
          "hidden text-[10px] font-medium uppercase tracking-wider sm:inline",
          overall === "ok" && "text-ok",
          overall === "warn" && "text-warn",
          overall === "critical" && "text-critical",
        )}>
          {overall}
        </span>
        <ChevronDown size={12} className="shrink-0 text-text-faint" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-9 z-50 min-w-[260px] overflow-hidden rounded-md border border-edge bg-surface shadow-xl">
            {isLive ? (
              liveCanSwap ? (
                <>
                  <div className="border-b border-edge px-3 py-2 text-[10px] uppercase tracking-wider text-text-faint">{t("topbar.switchSiteLive")}</div>
                  {availableSites.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => { setSite(s.id); setOpen(false); }}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-surface-2",
                        s.id === topologyId && "bg-accent/10 text-accent",
                      )}
                    >
                      <div>
                        <div className="font-semibold">{s.name}</div>
                        <div className="text-[10px] text-text-faint">{t("topbar.emsSite")} · {s.id.slice(0, 8)}…</div>
                      </div>
                      {s.id === topologyId && <Globe size={12} />}
                    </button>
                  ))}
                  <div className="border-t border-edge bg-surface-2 px-3 py-2 text-[10px] text-text-muted">
                    <Activity size={11} className="mr-1 inline -translate-y-0.5" />
                    {t("topbar.sitesWithBess")}
                  </div>
                </>
              ) : (
                <>
                  <div className="border-b border-edge px-3 py-2 text-[10px] uppercase tracking-wider text-text-faint">{t("topbar.activeSite")}</div>
                  <div className="px-3 py-3">
                    <div className="text-xs font-semibold">{topology?.name ?? t("topbar.connectingToEms")}</div>
                    <div className="mt-1 text-[10px] text-text-muted">
                      {topology
                        ? t("topbar.siteMeta", {
                            bess: topology.bess.length,
                            meters: topology.smartmeters.length,
                            pv: topology.smartmeters.some((m) => m.role === "pv") ? t("topbar.pv") : t("topbar.noPv"),
                          })
                        : t("topbar.waitingForCtx")}
                    </div>
                  </div>
                  <div className="border-t border-edge bg-surface-2 px-3 py-2 text-[10px] text-text-muted">
                    {t("topbar.bindHint")}
                  </div>
                </>
              )
            ) : (
              <>
                <div className="border-b border-edge px-3 py-2 text-[10px] uppercase tracking-wider text-text-faint">{t("topbar.switchSite")}</div>
                {TOPOLOGIES.map((topo) => (
                  <button
                    key={topo.id}
                    onClick={() => { setSite(topo.id); setOpen(false); }}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-surface-2",
                      topo.id === topologyId && "bg-accent/10 text-accent",
                    )}
                  >
                    <div>
                      <div className="font-semibold">{topo.name}</div>
                      <div className="text-[10px] text-text-faint">
                        {t("topbar.siteMeta", {
                          bess: topo.bess.length,
                          meters: topo.smartmeters.length,
                          pv: topo.smartmeters.some((m) => m.role === "pv") ? t("topbar.pv") : t("topbar.noPv"),
                        })}
                      </div>
                    </div>
                    {topo.id === topologyId && <Globe size={12} />}
                  </button>
                ))}
                <div className="border-t border-edge bg-surface-2 px-3 py-2 text-[10px] text-text-muted">
                  {/* `mode` ("mock"/"live") is a wire value — shown verbatim. */}
                  <div className="flex items-center gap-1.5"><Activity size={11} /> {t("topbar.dataSource")}: <strong>{mode}</strong></div>
                  <div className="flex items-center gap-1.5"><Beaker size={11} /> {t("topbar.appendLive.pre")} <code className="rounded bg-surface px-1 py-0.5">?live=1</code> {t("topbar.appendLive.post")}</div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
